import React from 'react';
import Dropzone from 'react-dropzone';
import { inject, observer } from 'mobx-react';
import { AxlButton, AxlInput, AxlReselect } from 'axl-reactjs-ui';
import _ from 'lodash';
import moment from 'moment-timezone';
import Papa from "papaparse";

// Utils
import timezoneOptions from '../../../constants/timezone';
import {requiredHeaders, multiNameRequiredHeaders} from "./csvConfig";

// Styles
import styles, * as E from './styles';

@inject('jobStore')
@observer
class ShipmentUploadForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      files: [],
      errors: [],
      name: '',
      timezone: {label: moment.tz.guess(), value: moment.tz.guess()}
    }
  }

  changeName = (e) => {
    this.setState({name: e.target.value});
  };

  pingJob = (job) => {
    const {jobStore} = this.props;
    jobStore.getDraftJob(job.id, (respJob) => {
      if (jobStore.isProcessing(respJob.status)) {
        setTimeout(() => this.pingJob(respJob), 1000)
      }
    })
  };

  upload = () => {
    const {jobStore, onClose} = this.props;
    jobStore.upload(this.state.files[0], this.state.name, this.state.timezone.value, (job) => {
      this.pingJob(job);
      onClose();
    }, (mapError) => {
      console.log('got error', mapError);
      this.setState({mapError});
    });
  };

  onDrop = (acceptFiles) => {
    if(acceptFiles.length > 0) {
      this.setState({files: acceptFiles, mapError: null});
      Papa.parse(acceptFiles[0], {
        header: true,
        step: (res, parser) => {
          const errors = [];
          const missingRequiredHeaders = _.difference(requiredHeaders, res.meta.fields);
          const missingMultiNameHeaders = multiNameRequiredHeaders.filter(headers => {
            const missing = _.difference(headers, res.meta.fields);
            return missing.length === headers.length;
          });

          if (missingRequiredHeaders.length > 0) {
            errors.push(`Missing required fields ${missingRequiredHeaders.join(', ')}`);
          }
          if (missingMultiNameHeaders.length > 0) {
            missingMultiNameHeaders.forEach(headers => {
              errors.push(`Missing required field, should be one of ${headers.join(' or ')}`);
            })
          }

          if (errors.length > 0) {
            this.setState({errors});
            parser.abort();
          }
        }
      })
    }
  };

  onDropRejected = () => {
    this.setState({errors: ['Maximum file upload size is 15MB.']});
  }

  onDropAccepted = () => {
    this.setState({errors: []});
  }

  onSelectTimezone = (selectTimezone) => {
    this.setState({timezone: selectTimezone});
  };

  render() {
    const { files, errors, mapError } = this.state;
    const {jobStore} = this.props;
    const { uploading, shipmentForm} = jobStore;

    const timezones = Object.keys(timezoneOptions).map( (tzName) => {
      return {label: tzName, value: timezoneOptions[tzName]};
    } );

    return(
      <E.Container>
        <E.Title>{`Upload new shipment batch`}</E.Title>
        <E.SubTitle>{`BATCH NAME`}</E.SubTitle>
        <E.GroupControl>
          <AxlInput value={this.state.name} onChange={this.changeName} type={`text`} style={styles.Input}/>
        </E.GroupControl>
        <E.SubTitle>{`UPLOAD CSV`}</E.SubTitle>
        <E.Text dangerouslySetInnerHTML={{ __html: `Upload a batch of shipments as a CSV file. Each row should be a shipment with pickup and dropoff details in the format shown in the<br />sample CSVs provided below.` }} />
        <br />
        <E.Text dangerouslySetInnerHTML={{ __html: `Download sample CSV files: <a href=${import.meta.env.REACT_APP_EMBED_URL + '/simple-sample-input'} target="_blank" rel="noopener">simple</a> or <a href=${import.meta.env.REACT_APP_EMBED_URL + '/advanced-sample-input'} target="_blank" rel="noopener">advanced</a>.` }} />
        <Dropzone accept={{ 'text/csv': '.csv' }} multiple={false} maxSize={15 * 1024 * 1024} onDrop={this.onDrop}
          onDropRejected={this.onDropRejected} onDropAccepted={this.onDropAccepted}>
          {({getRootProps, getInputProps}) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <E.Action>
                <AxlButton compact bg={`blueGray`} style={styles.uploadAction} >{`Upload File`}</AxlButton>
                {files.length > 0 && <E.FileName>{files[0].name}</E.FileName>}
              </E.Action>
            </div>
          )}
        </Dropzone>
        {errors.length > 0 && <E.ListErrors>
          {_.map(errors, (error, index) => <E.ErrorItem key={index}>
            {error}
          </E.ErrorItem>)}
          </E.ListErrors>}
        <E.GroupControlSelect>
          <E.SubTitle>{`TIME ZONE`}</E.SubTitle>
          <AxlReselect value={this.state.timezone} onChange={this.onSelectTimezone} placeholder={'Select timezone'} theme={`main`} options={timezones} />
        </E.GroupControlSelect>
        {mapError && <E.ListErrors>
          {_.map(mapError, (rows, message) => <E.ErrorItem key={message}>
            {message}{(rows && rows.length > 0) ? rows.length > 5 ? ` on ${rows.length} rows.`: ` on rows [${rows.filter((row, idx) => rows.indexOf(row) === idx).join(", ")}].`: ''}
          </E.ErrorItem>)}
          </E.ListErrors>}
        <E.Actions>
          <AxlButton compact bg={`blueGray`} style={styles.actions} onClick={this.props.onClose}>{`Cancel`}</AxlButton>
          <AxlButton disabled={this.state.files.length <= 0 || this.state.name.trim() == '' || errors.length > 0} compact bg={`periwinkle`} style={styles.actions} onClick={this.upload} loading={uploading}>{`Upload`}</AxlButton>
        </E.Actions>
      </E.Container>
    );
  }
}

export default ShipmentUploadForm;